import { useContext } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { composeRenderProps, ListBox, ListBoxSection } from '@launchpad-ui/components';
import clsx from 'clsx';

import { SearchCategoriesContext } from './contexts';
import type { SearchCategoriesProps } from './types';

import styles from './ResourceSelector.module.css';

// Helper function to check if items is a nested structure
function isNestedItems<T>(items: Iterable<T> | Iterable<Iterable<T>>): items is Iterable<Iterable<T>> {
  const firstItem = Array.from(items as Iterable<unknown>)[0];
  return firstItem !== null && typeof firstItem === 'object' && Symbol.iterator in firstItem;
}

// Slot component for ResourceSelector
export function SearchCategories<T extends object>({ items, children, slot, ...props }: SearchCategoriesProps<T>) {
  // Get props from context when used as a slot
  const contextProps = useContext(SearchCategoriesContext);

  // Check if we have grouped categories
  if (isNestedItems(items)) {
    return (
      <div className={styles.categoriesArea}>
        <ListBox
          items={items}
          {...contextProps}
          {...props}
          selectionMode="single"
          className={composeRenderProps(props.className, (className) => clsx(className, styles.categories))}
          aria-label={props['aria-label'] || 'Categories'}
        >
          {items.map((group, groupIndex) => (
            <ListBoxSection
              key={groupIndex}
              className={styles.categoryGroup}
              aria-label={msg('ui.searchCategories.groupLabel', { number: groupIndex + 1 })}
            >
              {group.map((item) => children(item))}
            </ListBoxSection>
          ))}
        </ListBox>
      </div>
    );
  }

  // Original flat structure
  return (
    <div className={styles.categoriesArea}>
      <ListBox<T>
        {...contextProps}
        {...props}
        items={items}
        selectionMode="single"
        className={composeRenderProps(props.className, (className) => clsx(className, styles.categories))}
        aria-label={props['aria-label'] || 'Categories'}
      >
        {children}
      </ListBox>
    </div>
  );
}
